import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import '@fortawesome/fontawesome-free/css/all.min.css';
import { Container, Row, Col, Button, Card, CardBody } from "reactstrap";
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import PlanSelectionModal from "../Components/PlanSelectionModal";

import './SubscriptionDetail.css'

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const SubscriptionDetails = () => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState('standard');
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({ skipPlanSelection: false, selectedPlan: null, action: 'upgrade' });
  const history = useHistory();

  const formatDate = (dateString) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setError("No authentication token found. Please log in.");
      toast.error("No authentication token found. Please log in.");
      history.push("/login");
      return;
    }

    fetchSubscription(token);
  }, [history]);

  const formatSubscriptionMethod = (method) => {
    if (!method) return 'Not available';

    switch (method.toLowerCase()) {
      case 'stripe':
        return 'Credit Card';
      case 'e-transfer':
        return 'E-Transfer';
      default:
        return capitalizeFirstLetter(method);
    }
  };

  const fetchSubscription = async (token) => {
    try {
      const response = await axiosInstance.get("/business/payment/subscription", {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const responseData = response.data?.responseData;
      setSubscriptionData(responseData?.subscription || null);
      
      // Set subscription type from API response
      if (responseData?.subscriptionType) {
        setSubscriptionType(responseData.subscriptionType);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.responseMessage?.[0] ||
        err.message ||
        "Failed to fetch subscription details";
      setError(errorMessage);
      toast.error(errorMessage);

      if (err.response?.status === 401) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("authUser");
        history.push("/login");
      }
    }
  };

  const cancelSubscription = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("No authentication token found. Please log in.");
        history.push("/login");
        return;
      }

      const response = await axiosInstance.delete("/business/payment/card", {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(response.data?.responseMessage?.[0] || "Subscription cancelled successfully");

      await fetchSubscription(token);

      if (subscriptionData?.subscriptionStatus === 'expired') {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("authUser");
        history.push("/login");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.responseMessage?.[0] ||
        err.message ||
        "Failed to cancel subscription";
      toast.error(errorMessage);

      if (err.response?.status === 401) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("authUser");
        history.push("/login");
      }
    }
  };

  const handleUpgradeClick = () => {
    // Check if subscription exists and payment card exists
    const hasSubscription = subscriptionData !== null;
    const hasPaymentCard = subscriptionData?.paymentCard !== null && subscriptionData?.paymentCard !== undefined;
    
    if (hasSubscription && !hasPaymentCard) {
      // Scenario 1: Subscription exists but no payment card - Add Card
      // Skip package selection and go directly to card details step
      const currentPlanType = subscriptionType === 'standard' ? 1 : 
                             subscriptionType === 'sponsored' ? 2 : 1;
      setIsPlanModalOpen(true);
      // We'll need to pass this info to the modal to skip step 1
      setModalConfig({ skipPlanSelection: true, selectedPlan: currentPlanType, action: 'addCard' });
    } else if (hasSubscription && hasPaymentCard) {
      // Scenario 2: Subscription exists and payment card exists - Update Card
      // Skip package selection and go directly to card details step
      const currentPlanType = subscriptionType === 'standard' ? 1 : 
                             subscriptionType === 'sponsored' ? 2 : 1;
      setIsPlanModalOpen(true);
      setModalConfig({ skipPlanSelection: true, selectedPlan: currentPlanType, action: 'updateCard' });
    } else {
      // Fallback to normal modal flow
      setIsPlanModalOpen(true);
      setModalConfig({ skipPlanSelection: false, selectedPlan: null, action: 'upgrade' });
    }
  };

  const handleChangePlanClick = () => {
    // Check if payment card exists
    const hasPaymentCard = subscriptionData?.paymentCard !== null && subscriptionData?.paymentCard !== undefined;
    
    if (hasPaymentCard) {
      // Scenario 3.1: Card exists - just select package and skip card details
      setIsPlanModalOpen(true);
      setModalConfig({ skipPlanSelection: false, selectedPlan: null, action: 'changePlan', skipCardDetails: true });
    } else {
      // Scenario 3.2: No card - select package and go to card details
      setIsPlanModalOpen(true);
      setModalConfig({ skipPlanSelection: false, selectedPlan: null, action: 'changePlan', skipCardDetails: false });
    }
  };

  // Function to refresh subscription data after successful payment
  const refreshSubscriptionData = () => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      fetchSubscription(token);
    }
  };

  const getStatusBadgeClass = (type, value) => {
    if (type === 'status') {
      switch (value.toLowerCase()) {
        case 'expired':
          return 'bg-danger';
        case 'subscribed':
          return 'bg-primary';
        default:
          return 'bg-secondary';
      }
    } else if (type === 'method') {
      return 'bg-dark';
    }
  };

  const calculateNextPaymentDate = (expiryDate) => {
    if (!expiryDate) return null;

    const expiry = new Date(expiryDate);
    const nextPayment = new Date(expiry);
    nextPayment.setDate(nextPayment.getDate() + 1); // Add exactly 1 day

    return nextPayment;
  };

  const getPlanPrice = (planType) => {
    const planPricing = {
      'standard': 20,
      'sponsored': 100
    };
    return planPricing[planType] || 20;
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const canCancelSubscription = subscriptionData?.paymentCardId || subscriptionData?.paymentCard?._id;

  const isStripeSubscription = subscriptionData?.subscriptionMethod?.toLowerCase() === 'stripe';
  const isCashOrETransfer = ['cash', 'e-transfer'].includes(subscriptionData?.subscriptionMethod?.toLowerCase());
  const isSubscribed = subscriptionData?.subscriptionStatus?.toLowerCase() === 'subscribed';
  const isExpired = subscriptionData?.subscriptionStatus?.toLowerCase() === 'expired';

  return (
    <div className="page-content">
      <Container fluid className="mt-5">
        <MetaTags>
          <title>Subscription Details | Admin Panel</title>
        </MetaTags>
        <Row className="align-items-center topbp">
          <Col xl={6} className="d-flex align-items-center">
            <h3 className="bp-title mb-0">Subscription Details</h3>
          </Col>
        </Row>

        <div className="bp-separator" />

        <Row className="mt-4">
          <Col xl={12}>
            {error ? (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            ) : subscriptionData ? (
              <Card className='mt-3'>
                <CardBody>
                  <Row className="mb-4 profile-header">
                    <Col md={12}>
                      <div className="row align-items-center mb-5 subscription-header">
                        <div className="col-12 col-md-6 subscription-title">
                          <h3 className="subshead">Subscription Plan</h3>
                        </div>
                        {/* <div className="col-12 col-md-6 subscription-actions">
                          {!isExpired && !isCashOrETransfer && (
                            <>
                              <button
                                className="btn btn-outline-danger me-2"
                                onClick={cancelSubscription}
                                disabled={!canCancelSubscription || isExpired}
                              >
                                {canCancelSubscription ? 'Cancel Subscription' : 'No Active Subscription'}
                              </button>
                              {isStripeSubscription && (
                                <button
                                  className="btn btn-secondary me-2"
                                  onClick={handleUpgradeClick}
                                  disabled={isExpired}
                                >
                                  {subscriptionData?.paymentCard ? 'Update Card' : 'Add Card'}
                                </button>
                              )}
                              {isStripeSubscription && (
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={handleChangePlanClick}
                                  disabled={isExpired}
                                >
                                  Change Plan
                                </button>
                              )}
                            </>
                          )}
                        </div> */}


     <div className="col-12 col-md-6 subscription-actions">

     {canCancelSubscription && (
  <button
    className="btn btn-outline-danger me-2"
    onClick={cancelSubscription}
    disabled={!canCancelSubscription || isExpired}
  >
    Cancel Subscription
  </button>
)}

                           <button
                                  className="btn btn-secondary me-2"
                                  onClick={handleUpgradeClick}
                                  disabled={isExpired}
                                >
                                  {subscriptionData?.paymentCard ? 'Update Card' : 'Add Card'}
                                </button>
                           <button
                                  className="btn btn-outline-primary"
                                  onClick={handleChangePlanClick}
                                  disabled={isExpired}
                                >
                                  Change Plan
                                </button>
                       
                    
                        </div>




                      </div>

                      <div className="subscription-dates mt-2">
                        <p className="mb-1 date d-flex align-items-center gap-2">
                          <span className="innerdate">Subscription Type:</span>
                          <span>
                            {capitalizeFirstLetter(subscriptionType)} (${getPlanPrice(subscriptionType)}/month)
                          </span>
                        </p>
                        {/* <p className="mb-1 date d-flex align-items-center gap-2">
                          <span className="innerdate">Subscription Status:</span>
                          <span className={`badge ${getStatusBadgeClass('status', subscriptionData.subscriptionStatus)}`}>
                            {capitalizeFirstLetter(subscriptionData.subscriptionStatus)}
                          </span>
                        </p> */}
                        <p className="mb-1 date d-flex align-items-center gap-2">
                          <span className="innerdate">Subscription Method:</span>
                          <span>
                            {formatSubscriptionMethod(subscriptionData.subscriptionMethod)}
                          </span>
                        </p>
                      </div>

                      <div className="subscription-dates mt-3">
                        {isStripeSubscription && isSubscribed ? (
                          <>
                            <p className="mb-1 date d-flex align-items-center gap-2">
                              <span className="innerdate">Expires/Renews on:</span>
                              <span>
                                {formatDate(subscriptionData.subscriptionExpiryDate)}
                              </span>
                            </p>
                            {/* <p className="mb-1 date d-flex align-items-center gap-2">
                              <span className="innerdate">Next Payment:</span>
                              <span>
                                ${getPlanPrice(subscriptionType)} on {formatDate(
                                  subscriptionData.nextBillingDate ||
                                  calculateNextPaymentDate(subscriptionData.subscriptionExpiryDate)
                                )}
                              </span>
                            </p> */}
                          </>
                        ) : isCashOrETransfer && isSubscribed ? (
                          <>
                            <p className="mb-1 date d-flex align-items-center gap-2">
                              <span className="innerdate">Expiring on:</span>
                              <span>
                                {formatDate(subscriptionData.subscriptionExpiryDate)}
                              </span>
                            </p>
                          </>
                        ) : isStripeSubscription && isExpired ? (
                          <>
                            <p className="mb-1 date d-flex align-items-center gap-2">
                              <span className="innerdate">Expired on:</span>
                              <span>
                                {formatDate(subscriptionData.subscriptionExpiryDate)}
                              </span>
                            </p>
                            <p className="mb-1 date d-flex align-items-center gap-2 text-danger">
                              <span className="innerdate">Please update your card and pay ${getPlanPrice(subscriptionType)} to subscribe again</span>
                            </p>
                          </>
                        ) : isCashOrETransfer && isExpired ? (
                          <div className="d-flex flex-column align-items-left">
                            <p className="mb-1 date d-flex align-items-center gap-2">
                              <span className="innerdate">Expired on:</span>
                              <span>
                                {formatDate(subscriptionData.subscriptionExpiryDate)}
                              </span>
                            </p>
                            <p className="mb-1 mt-2 text-danger innerdate date">
                              To renew your subscription, please send an email to
                              <a href="mailto:contact@findgram.ca" className="fw-semibold ms-1 fs-5 text-secondary">contact@findgram.ca</a>
                            </p>
                          </div>
                        ) : (
                          <>
                            <p className="mb-1 date d-flex align-items-center gap-2">
                              <span className="innerdate">Start Date:</span>
                              <span>
                                {formatDate(subscriptionData.subscriptionStartDate)}
                              </span>
                            </p>
                            <p className="mb-1 date d-flex align-items-center gap-2">
                              <span className="innerdate">Expiry Date:</span>
                              <span>
                                {formatDate(subscriptionData.subscriptionExpiryDate)}
                              </span>
                            </p>
                          </>
                        )}

                        <div className="payment-card-info">
                          {canCancelSubscription ? (
                            <span className="innerdate">
                              Card: {subscriptionData.paymentCard?.cardBrand?.toUpperCase()} **** **** **** {subscriptionData.paymentCard?.cardLast4Number}
                            </span>
                          ) : !isCashOrETransfer && (
                            <h5 className="subshead text-danger">
                              No payment method
                            </h5>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            ) : (
              <></>
            )}
          </Col>
        </Row>
      </Container>
      <ToastContainer />
      
      {/* Plan Selection Modal */}
      <PlanSelectionModal 
        isOpen={isPlanModalOpen}
        toggle={() => setIsPlanModalOpen(!isPlanModalOpen)}
        isUpgrade={true}
        onSubscriptionSuccess={refreshSubscriptionData}
        modalConfig={modalConfig}
      />
    </div>
  );
};

export default SubscriptionDetails;